import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getStoredClientId } from "../utils/tokenUtils";
import { migrationService } from "../services/migrationService";
import { projectService } from "../services/projectService";
import { Button } from "../components/ui/button";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  HelpCircle,
  ExternalLink,
  List,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { InlineLoadSkeleton } from "../components/common/InlineLoadSkeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import toast from "react-hot-toast";

/** Help content: where to get URL, username/email, API key for each source */
const CREDENTIALS_HELP = {
  testrail: {
    title: "Where to get TestRail credentials",
    url: "Your TestRail instance URL. Example: https://yourcompany.testrail.io or https://yourcompany.testrail.com. Use the same URL you use to open TestRail in the browser (without /index.php).",
    email: "The email address you use to log in to TestRail.",
    apiKey:
      'Go to TestRail → Profile (top-right) → API Keys (or Account Settings). Click "Add API Key", copy the key. If you don\'t see it, your admin may need to enable API access.',
    link: "https://www.gurock.com/testrail/docs/api/getting_started",
  },
  tuskr: {
    title: "Where to get Tuskr credentials",
    url: "Your Tuskr instance URL. Example: https://tuskr.live/ or your self-hosted Tuskr URL. Use the base URL you use to open Tuskr in the browser.",
    apiKey:
      'Log in to Tuskr → User menu (top-right) → API Token or Account Settings. Create or copy your API token. Some versions use "API Key" under Integrations or Settings.',
    link: "https://tuskr.app/help/api",
  },
  browserstack: {
    title: "Where to get BrowserStack Test Management credentials",
    url: "Usually https://test-management.browserstack.com/ (default). For Test Management it may be the same or your org's BrowserStack dashboard URL.",
    username:
      'Your BrowserStack username. Find it in BrowserStack Dashboard → Account → Profile, or in the "Username" field shown after login.',
    accessKey:
      'BrowserStack Access Key. Go to BrowserStack Dashboard → Account → Settings (or Profile) → "Access Keys" / "Automate" section. Copy the Access Key (different from password).',
    link: "https://www.browserstack.com/docs/test-management/api",
  },
};

const SOURCES = [
  { id: "testrail", label: "TestRail" },
  { id: "tuskr", label: "Tuskr" },
  { id: "browserstack", label: "BrowserStack Test Management" },
  { id: "csv", label: "CSV" },
  { id: "excel", label: "Excel" },
];

const DUPLICATE_OPTIONS = [
  { id: "skip", label: "Skip duplicates" },
  { id: "overwrite", label: "Overwrite existing" },
  { id: "create_copy", label: "Create new copy" },
];

function getCredentialsPayload(source, credentials) {
  const normalizedUrl = (credentials?.url || "").trim().replace(/\/+$/, "");
  if (source === "testrail") {
    return {
      url: normalizedUrl,
      email: credentials.email,
      api_key: credentials.api_key,
    };
  }
  if (source === "tuskr") {
    return {
      url: normalizedUrl,
      api_key: credentials.api_key || credentials.token,
    };
  }
  if (source === "browserstack") {
    return {
      url: normalizedUrl || "https://test-management.browserstack.com",
      username: credentials.username,
      access_key: credentials.access_key || credentials.api_key,
    };
  }
  return {};
}

export default function MigrationWizardPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const clientId =
    searchParams.get("client_id") || getStoredClientId() || user?.client_id;

  const [step, setStep] = useState(1);
  const [source, setSource] = useState("");
  const [credentials, setCredentials] = useState({
    url: "",
    email: "",
    api_key: "",
    username: "",
    access_key: "",
    token: "",
  });
  const [fileRef, setFileRef] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [metadataProjects, setMetadataProjects] = useState([]);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedSections, setSelectedSections] = useState({});
  const [targetProjectIdForSections, setTargetProjectIdForSections] =
    useState("");
  const [tnProjects, setTnProjects] = useState([]);
  const [selectModalOpen, setSelectModalOpen] = useState(false);
  const [expandedProjectId, setExpandedProjectId] = useState(null);
  const [duplicateHandling, setDuplicateHandling] = useState("skip");
  const [starting, setStarting] = useState(false);

  const isFileSource = source === "csv" || source === "excel";

  // Load target projects when on step 3 or when Select modal opens, so dropdown has list ready
  useEffect(() => {
    if (!clientId || (step !== 3 && !selectModalOpen)) return;
    projectService
      .getProjects({ client_id: clientId, limit: 500, is_active: true })
      .then((data) => {
        const list =
          data?.data?.projects ??
          data?.projects ??
          (Array.isArray(data?.data) ? data.data : []);
        setTnProjects(Array.isArray(list) ? list : []);
      })
      .catch(() => setTnProjects([]));
  }, [clientId, step, selectModalOpen]);

  const handleFileSelect = async (e) => {
    const file = e.target?.files?.[0];
    if (!file || !clientId) return;
    setUploadingFile(true);
    try {
      const res = await migrationService.uploadFile(file, clientId);
      setFileRef(res.file_ref || null);
      toast.success("File uploaded");
    } catch (err) {
      toast.error(err.response?.data?.error || "Upload failed");
    } finally {
      setUploadingFile(false);
    }
  };

  const fetchMetadataAndGoToStep3 = async () => {
    if (!clientId || !source) return;
    if (isFileSource && !fileRef) {
      toast.error("Please upload a file first");
      return;
    }
    if (!isFileSource) {
      if (
        source === "testrail" &&
        (!credentials.url || !credentials.email || !credentials.api_key)
      ) {
        toast.error("TestRail: URL, email, and API key are required");
        return;
      }
      if (source === "tuskr" && (!credentials.url || !credentials.api_key)) {
        toast.error("Tuskr: URL and API key are required");
        return;
      }
      if (
        source === "browserstack" &&
        (!credentials.username || !credentials.access_key)
      ) {
        toast.error("BrowserStack: username and access key are required");
        return;
      }
    }
    setMetadataLoading(true);
    try {
      const body = { source, client_id: clientId };
      if (isFileSource) body.file_ref = fileRef;
      else body.credentials = getCredentialsPayload(source, credentials);
      const res = await migrationService.getMetadata(body);
      setMetadataProjects(res.projects || []);
      setStep(3);
    } catch (err) {
      toast.error(
        err.response?.data?.error || "Failed to load projects from source",
      );
    } finally {
      setMetadataLoading(false);
    }
  };

  const toggleProjectSelection = (proj) => {
    setSelectedProjects((prev) =>
      prev.some((p) => p.id === proj.id)
        ? prev.filter((p) => p.id !== proj.id)
        : [...prev, { id: proj.id, name: proj.name || proj.id }],
    );
    setSelectedSections((prev) => {
      const next = { ...prev };
      delete next[proj.id];
      return next;
    });
  };

  const toggleSectionSelection = (
    sourceProjectId,
    projectName,
    sectionId,
    sectionName,
  ) => {
    setSelectedSections((prev) => {
      const cur = prev[sourceProjectId] || { sectionIds: [], projectName };
      const has = cur.sectionIds.includes(sectionId);
      const sectionIds = has
        ? cur.sectionIds.filter((id) => id !== sectionId)
        : [...cur.sectionIds, sectionId];
      if (sectionIds.length === 0) {
        const next = { ...prev };
        delete next[sourceProjectId];
        return next;
      }
      return { ...prev, [sourceProjectId]: { sectionIds, projectName } };
    });
    setSelectedProjects((prev) => prev.filter((p) => p.id !== sourceProjectId));
  };

  const selectAllProjects = () => {
    if (selectedProjects.length >= metadataProjects.length) {
      setSelectedProjects([]);
      setSelectedSections({});
    } else {
      setSelectedProjects(
        metadataProjects.map((p) => ({ id: p.id, name: p.name || p.id })),
      );
      setSelectedSections({});
    }
  };

  const hasSectionSelection = Object.keys(selectedSections).length > 0;
  const sectionSelectionCount = Object.values(selectedSections).reduce(
    (sum, s) => sum + (s.sectionIds?.length ?? 0),
    0,
  );

  const totalSelectedTestCases =
    selectedProjects.reduce((sum, sel) => {
      const p = metadataProjects.find((m) => m.id === sel.id);
      return sum + (p?.test_cases ?? 0);
    }, 0) +
    Object.entries(selectedSections).reduce((sum, [pid, s]) => {
      const proj = metadataProjects.find((m) => m.id === pid);
      const suitesDetail = proj?.suites_detail || [];
      let count = 0;
      for (const suite of suitesDetail) {
        for (const sec of suite.sections || []) {
          if (s.sectionIds?.includes(sec.id)) count += sec.test_cases ?? 0;
        }
      }
      return sum + count;
    }, 0);

  const handleStart = async () => {
    const hasProjects = selectedProjects.length > 0;
    const hasSections = hasSectionSelection && targetProjectIdForSections;
    if (!clientId || !source || (!hasProjects && !hasSections)) {
      toast.error(
        hasSectionSelection
          ? "Select a target project for the selected sections"
          : "Select at least one project or sections to migrate",
      );
      return;
    }
    if (hasSectionSelection && !targetProjectIdForSections) {
      toast.error("Select a target project for the selected sections");
      return;
    }
    setStarting(true);
    try {
      const creds = isFileSource
        ? null
        : getCredentialsPayload(source, credentials);
      let started = 0;
      for (const proj of selectedProjects) {
        const body = {
          source,
          client_id: clientId,
          scope: {
            source_project_id: proj.id,
            project_name: proj.name || proj.id,
          },
          duplicate_handling: duplicateHandling,
          options: {},
        };
        if (isFileSource) body.file_ref = fileRef;
        else body.credentials = creds;
        await migrationService.startMigration(body);
        started++;
      }
      for (const [sourceProjectId, sel] of Object.entries(selectedSections)) {
        if (!sel.sectionIds?.length || !targetProjectIdForSections) continue;
        const body = {
          source,
          client_id: clientId,
          project_id: targetProjectIdForSections,
          scope: { project_id: sourceProjectId, section_ids: sel.sectionIds },
          duplicate_handling: duplicateHandling,
          options: {},
        };
        if (isFileSource) body.file_ref = fileRef;
        else body.credentials = creds;
        await migrationService.startMigration(body);
        started++;
      }
      toast.success(
        started === 1 ? "Migration queued" : `${started} migrations queued`,
      );
      navigate(`/migration?client_id=${clientId}`);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to start migration");
    } finally {
      setStarting(false);
    }
  };

  if (!clientId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">
          Open Migration from the client Projects page to start.
        </p>
        <Button variant="link" onClick={() => navigate("/migration")}>
          Back to Migration
        </Button>
      </div>
    );
  }

  const stepLabels = [
    "Source",
    "Connect",
    "Select projects",
    "Duplicate handling",
  ];
  const maxStep = 4;

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-bold mb-6">Migration Wizard</h1>

      <div className="flex gap-2 mb-6 flex-wrap">
        {stepLabels.map((label, i) => (
          <div
            key={i}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm ${step >= i + 1 ? "bg-primary text-primary-foreground" : "bg-muted"}`}
          >
            {step > i + 1 ? <Check className="h-4 w-4" /> : i + 1}
            <span>{label}</span>
          </div>
        ))}
      </div>

      {step === 1 && (
        <>
          <p className="text-sm text-muted-foreground mb-4">
            Select the source to migrate from.
          </p>
          <div className="flex flex-col gap-2">
            {SOURCES.map((s) => (
              <label
                key={s.id}
                className="flex items-center gap-3 p-3 border rounded-md cursor-pointer hover:bg-muted/50"
              >
                <input
                  type="radio"
                  name="source"
                  checked={source === s.id}
                  onChange={() => setSource(s.id)}
                  className="rounded-full"
                />
                <span>{s.label}</span>
              </label>
            ))}
          </div>
          <div className="mt-6 flex justify-end">
            <Button onClick={() => setStep(2)} disabled={!source}>
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <p className="text-sm text-muted-foreground mb-4">
            {isFileSource
              ? "Upload your CSV or Excel file."
              : "Enter connection details (stored only for this migration)."}
          </p>
          {isFileSource ? (
            <div>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className="text-sm mb-2"
              />
              {uploadingFile && (
                <InlineLoadSkeleton className="h-4 w-4 inline-block ml-2" />
              )}
              {fileRef && (
                <p className="text-sm text-green-600 mt-2">File uploaded.</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {CREDENTIALS_HELP[source] && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 p-4 text-sm">
                  <div className="flex items-center gap-2 font-medium text-blue-900 dark:text-blue-100 mb-3">
                    <HelpCircle className="h-4 w-4 shrink-0" />
                    {CREDENTIALS_HELP[source].title}
                  </div>
                  <ul className="space-y-2 text-blue-800 dark:text-blue-200">
                    <li>
                      <strong>URL:</strong>{" "}
                      <span className="text-muted-foreground dark:text-blue-300">
                        {CREDENTIALS_HELP[source].url}
                      </span>
                    </li>
                    {CREDENTIALS_HELP[source].email && (
                      <li>
                        <strong>Email:</strong>{" "}
                        <span className="text-muted-foreground dark:text-blue-300">
                          {CREDENTIALS_HELP[source].email}
                        </span>
                      </li>
                    )}
                    {CREDENTIALS_HELP[source].username && (
                      <li>
                        <strong>Username:</strong>{" "}
                        <span className="text-muted-foreground dark:text-blue-300">
                          {CREDENTIALS_HELP[source].username}
                        </span>
                      </li>
                    )}
                    {(CREDENTIALS_HELP[source].apiKey ||
                      CREDENTIALS_HELP[source].accessKey) && (
                      <li>
                        <strong>
                          {source === "browserstack" ? "Access Key" : "API Key"}
                          :
                        </strong>{" "}
                        <span className="text-muted-foreground dark:text-blue-300">
                          {CREDENTIALS_HELP[source].apiKey ||
                            CREDENTIALS_HELP[source].accessKey}
                        </span>
                      </li>
                    )}
                  </ul>
                  {CREDENTIALS_HELP[source].link && (
                    <a
                      href={CREDENTIALS_HELP[source].link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-3 text-blue-700 dark:text-blue-300 hover:underline"
                    >
                      Official API docs <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">URL</label>
                <input
                  type="url"
                  className="w-full border rounded px-3 py-2"
                  placeholder={
                    source === "browserstack"
                      ? "https://test-management.browserstack.com"
                      : "https://your-instance.testrail.io"
                  }
                  value={credentials.url}
                  onChange={(e) =>
                    setCredentials((c) => ({ ...c, url: e.target.value }))
                  }
                  onBlur={() =>
                    setCredentials((c) => ({
                      ...c,
                      url: (c.url || "").trim().replace(/\/+$/, ""),
                    }))
                  }
                />
              </div>
              {source === "testrail" && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      className="w-full border rounded px-3 py-2"
                      placeholder="you@company.com"
                      value={credentials.email}
                      onChange={(e) =>
                        setCredentials((c) => ({ ...c, email: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      API Key
                    </label>
                    <input
                      type="password"
                      className="w-full border rounded px-3 py-2"
                      placeholder="Paste API key from TestRail profile"
                      value={credentials.api_key}
                      onChange={(e) =>
                        setCredentials((c) => ({
                          ...c,
                          api_key: e.target.value,
                        }))
                      }
                    />
                  </div>
                </>
              )}
              {source === "tuskr" && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    API Key / Token
                  </label>
                  <input
                    type="password"
                    className="w-full border rounded px-3 py-2"
                    placeholder="Paste API token from Tuskr account"
                    value={credentials.api_key || credentials.token}
                    onChange={(e) =>
                      setCredentials((c) => ({
                        ...c,
                        api_key: e.target.value,
                        token: e.target.value,
                      }))
                    }
                  />
                </div>
              )}
              {source === "browserstack" && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Username
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded px-3 py-2"
                      placeholder="Your BrowserStack username"
                      value={credentials.username}
                      onChange={(e) =>
                        setCredentials((c) => ({
                          ...c,
                          username: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Access Key
                    </label>
                    <input
                      type="password"
                      className="w-full border rounded px-3 py-2"
                      placeholder="From BrowserStack Account → Access Keys"
                      value={credentials.access_key}
                      onChange={(e) =>
                        setCredentials((c) => ({
                          ...c,
                          access_key: e.target.value,
                        }))
                      }
                    />
                  </div>
                </>
              )}
            </div>
          )}
          <div className="mt-6 flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <Button
              onClick={fetchMetadataAndGoToStep3}
              disabled={
                metadataLoading ||
                (isFileSource
                  ? !fileRef
                  : source === "testrail"
                    ? !credentials.api_key
                    : source === "browserstack"
                      ? !credentials.access_key
                      : !credentials.api_key)
              }
            >
              {metadataLoading ? (
                <InlineLoadSkeleton className="h-4 w-4 mr-1" />
              ) : (
                <List className="h-4 w-4 mr-1" />
              )}
              Show Projects
            </Button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <p className="text-sm text-muted-foreground mb-4">
            Available projects from your source. Select which ones to import.
          </p>
          <div className="border rounded-md overflow-auto mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-2">Project</th>
                  <th className="text-left p-2">Suites</th>
                  <th className="text-left p-2">Sections</th>
                  <th className="text-left p-2">Test Cases</th>
                </tr>
              </thead>
              <tbody>
                {metadataProjects.map((p) => (
                  <tr key={p.id} className="border-b">
                    <td className="p-2">{p.name || p.id}</td>
                    <td className="p-2">{p.suites ?? "—"}</td>
                    <td className="p-2">{p.sections ?? "—"}</td>
                    <td className="p-2">{p.test_cases ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <Button onClick={() => setSelectModalOpen(true)}>
              <List className="h-4 w-4 mr-2" />
              Select Projects
            </Button>
            {(selectedProjects.length > 0 || hasSectionSelection) && (
              <span className="text-sm text-muted-foreground">
                {selectedProjects.length} project(s)
                {hasSectionSelection
                  ? `, ${sectionSelectionCount} section(s)`
                  : ""}{" "}
                · {totalSelectedTestCases} test cases
              </span>
            )}
          </div>
          <div className="mt-6 flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <Button
              onClick={() => setStep(4)}
              disabled={selectedProjects.length === 0 && !hasSectionSelection}
            >
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          <Dialog open={selectModalOpen} onOpenChange={setSelectModalOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Select Projects to Import</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 max-h-[60vh] overflow-auto">
                <label className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={
                      selectedProjects.length === metadataProjects.length &&
                      metadataProjects.length > 0 &&
                      !hasSectionSelection
                    }
                    onChange={selectAllProjects}
                  />
                  <span className="font-medium">Select all projects</span>
                </label>
                {metadataProjects.map((p) => {
                  const expanded = expandedProjectId === p.id;
                  const hasSuites = (p.suites_detail || []).length > 0;
                  const sectionSel = selectedSections[p.id];
                  const isProjectChecked = selectedProjects.some(
                    (s) => s.id === p.id,
                  );
                  return (
                    <div key={p.id} className="border rounded-md">
                      <div className="flex items-center gap-2 p-3">
                        {hasSuites && (
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedProjectId(expanded ? null : p.id)
                            }
                            className="p-0.5"
                          >
                            {expanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                        )}
                        <label className="flex items-center gap-3 flex-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isProjectChecked}
                            onChange={() => toggleProjectSelection(p)}
                          />
                          <span>
                            {p.name || p.id} ({p.test_cases ?? 0} test cases)
                          </span>
                        </label>
                      </div>
                      {expanded && hasSuites && (
                        <div className="pl-8 pr-3 pb-3 space-y-2 border-t">
                          {(p.suites_detail || []).map((suite) => (
                            <div key={suite.id}>
                              <div className="text-sm font-medium text-muted-foreground mt-2">
                                {suite.name || "Suite"}
                              </div>
                              {(suite.sections || []).map((sec) => {
                                const secChecked =
                                  sectionSel?.sectionIds?.includes(sec.id);
                                return (
                                  <label
                                    key={sec.id}
                                    className="flex items-center gap-2 pl-4 py-1 cursor-pointer hover:bg-muted/50 rounded"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={secChecked}
                                      onChange={() =>
                                        toggleSectionSelection(
                                          p.id,
                                          p.name || p.id,
                                          sec.id,
                                          sec.name,
                                        )
                                      }
                                    />
                                    <span className="text-sm">
                                      {sec.name || sec.id} (
                                      {sec.test_cases ?? 0})
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {hasSectionSelection && (
                <div className="pt-3 border-t">
                  <p className="text-sm font-medium mb-2">
                    Import selected sections into:
                  </p>
                  <Select
                    value={targetProjectIdForSections}
                    onValueChange={setTargetProjectIdForSections}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select target project" />
                    </SelectTrigger>
                    <SelectContent>
                      {tnProjects.length === 0 ? (
                        <div className="py-4 px-3 text-center text-muted-foreground text-sm">
                          No projects found
                        </div>
                      ) : (
                        tnProjects.map((tp) => (
                          <SelectItem
                            key={tp.project_id ?? tp.id}
                            value={String(tp.project_id ?? tp.id ?? "")}
                          >
                            {tp.name || tp.project_id || tp.id || "—"}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <p className="text-sm text-muted-foreground pt-2">
                Total test cases: {totalSelectedTestCases}
              </p>
              <div className="flex justify-end pt-2">
                <Button onClick={() => setSelectModalOpen(false)}>Done</Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}

      {step === 4 && (
        <>
          <p className="text-sm text-muted-foreground mb-4">
            Choose how to handle duplicates (same title + section).
          </p>
          <div className="flex flex-col gap-2 mb-6">
            {DUPLICATE_OPTIONS.map((o) => (
              <label
                key={o.id}
                className="flex items-center gap-3 p-3 border rounded-md cursor-pointer hover:bg-muted/50"
              >
                <input
                  type="radio"
                  name="duplicate"
                  checked={duplicateHandling === o.id}
                  onChange={() => setDuplicateHandling(o.id)}
                  className="rounded-full"
                />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {selectedProjects.length > 0 &&
              `${selectedProjects.length} project(s) will be created in TestNimbus. `}
            {hasSectionSelection &&
              `${sectionSelectionCount} section(s) will be imported into the selected target project.`}
          </p>
          <div className="mt-6 flex justify-between">
            <Button variant="outline" onClick={() => setStep(3)}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <Button
              onClick={handleStart}
              disabled={
                starting || (hasSectionSelection && !targetProjectIdForSections)
              }
            >
              {starting ? (
                <InlineLoadSkeleton className="h-4 w-4 mr-1" />
              ) : null}
              Start Migration
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
